import { Nullable } from "common/records";
import { RootState } from "common/reducers";
import { List, Map, Set } from "immutable";
import { DocumentRecord } from "common/records/Document";
import { EstimateLineItemRecord } from "common/records/EstimateLineItem";
import { EstimateGroupRecord } from "common/records/EstimateGroup";
import { EstimateLineItemOptionRecord } from "common/records/EstimateLineItemOption";
import { ImageRecord } from "common/records/Image";
import { estimate, lineItemEditingSnapshots } from "common/selectors/estimate.selectors";
import { byId as estimateLineItemOptionsById } from "common/selectors/estimateLineItemOption.selectors";
import { filteredEstimateGroups } from "common/selectors/estimateGroup.selectors";
import { byId as documentsById } from "common/selectors/document.selectors";
import { byId as imagesById } from "common/selectors/image.selectors";
import { createSelector } from "reselect";

const rootKey = "estimateLineItems";

/**
 * Loads byId key from estimateLineItems
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, EstimateLineItemRecord>} Map<number, EstimateLineItemRecord>
 */
export const byId = (state: RootState): Map<number, EstimateLineItemRecord> => state.getIn([rootKey, "byId"]);

/**
 * The transient source→clone line-item id map emitted by the estimate-level duplicate cascade. Keyed by
 * the original line item's id, valued by its clone's (negative) id. Empty outside a duplicate.
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, number>} Map<number, number>
 */
export const duplicateSourceToCloneIdMap = (state: RootState): Map<number, number> =>
  (state.getIn([rootKey, "duplicateSourceToCloneIdMap"]) as Map<number, number>) ?? Map<number, number>();

/**
 * Loads lastSavedById key from estimateLineItems (reflects the last server-persisted state).
 * Used to detect cross-group moves when building the save payload.
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, EstimateLineItemRecord>} Map<number, EstimateLineItemRecord>
 */
export const lastSavedLineItemsById = (state: RootState) => state.getIn([rootKey, "lastSavedById"]);

/**
 * Loads the line item by id
 *
 * @param {RootState} state The RootState
 * @param {{ estimateLineItemId: number }} props The estimate line item id
 * @returns {EstimateLineItemRecord | undefined} EstimateLineItemRecord | undefined
 */
export const estimateLineItem = (state: RootState, props: { estimateLineItemId: Nullable<number> }) =>
  props.estimateLineItemId ? byId(state).get(props.estimateLineItemId) : undefined;

/**
 * Loads the line item names as an array from a line item
 *
 * @param {RootState} state The RootState
 * @param {{ estimateLineItemId: number }} props The estimate line item id
 * @returns {string[]} string[]
 */
export const estimateLineItemNames = createSelector([estimateLineItem], (lineItem) => {
  if (!lineItem || !lineItem.name) {
    return [""];
  }
  const parts = lineItem.name.split(">");
  parts.map((part: string, index: number) => {
    parts[index] = part.trim();
  });

  return parts;
});

/**
 * Loads the current opening estimation ids
 *
 * @param {RootState} state The RootState
 * @param {{ estimateLineItemId: number }} props The estimate line item id
 * @returns {List<number>} List<number>
 */
export const estimateLineItemOpeningEstimationIds = createSelector(
  [estimateLineItem],
  (lineItem) => lineItem?.get("opening_estimation_ids") || List<number>(),
);

/**
 * Loads the estimate line items by estimateLineItemIds
 *
 * @param {RootState} state The RootState
 * @param {{ estimateLineItemIds: List<number> }} props The estimate line item ids
 * @returns {List<EstimateLineItemRecord>} List<EstimateLineItemRecord>
 */
export const estimateLineItems = (state: RootState, props: { estimateLineItemIds: List<number> }) =>
  props.estimateLineItemIds.map((id) => estimateLineItem(state, { estimateLineItemId: id }));

/**
 * Returns a filtered list of estimate line items by their ids and filter options.
 *
 * This selector retrieves estimate line items from the state using the provided ids, and applies filtering based on the `fullEstimate` option:
 * - If `fullEstimate` is true, all line items are returned regardless of their `_destroy` flag.
 * - Otherwise, line items with `_destroy` set to true are excluded.
 *
 * @param {RootState} state - The Redux root state
 * @param {{ estimateLineItemIds: List<number>; fullEstimate?: boolean }} props - Filtering options and line item ids
 * @returns {List<EstimateLineItemRecord>} List of filtered EstimateLineItemRecords
 */
export const filteredEstimateLineItems = createSelector(
  [estimateLineItems, (_, props: { estimateLineItemIds: List<number>; fullEstimate?: boolean }) => props],
  (lineItems, { fullEstimate = false }) => {
    return lineItems.filter(
      (lineItem) => lineItem && (fullEstimate || !lineItem._destroy),
    ) as List<EstimateLineItemRecord>;
  },
);

/**
 * Gets the line items in the estimate
 * Pass in the `includeAll` prop to get line items in all groups, not just the included ones.
 * @param {RootState} state The RootState
 * @param {{ estimateId: number; includeAll?: boolean; [key: string]: any }} props The props
 */
export const lineItemsEstimate = createSelector(
  [estimate, (state, props: { estimateId: number; includeAll?: boolean; [key: string]: any }) => ({ state, props })],
  (estimate, stateProps) => {
    const { state, props } = stateProps;
    if (!estimate) {
      return List<EstimateLineItemRecord>();
    }

    const groups = props.includeAll
      ? List([
          ...filteredEstimateGroups(state, { estimateGroupIds: estimate.group_ids, included: true }),
          ...filteredEstimateGroups(state, { estimateGroupIds: estimate.group_ids, included: false }),
        ])
      : filteredEstimateGroups(state, { estimateGroupIds: estimate.group_ids });

    const estimateLineItemIds = List<number>(
      groups
        .map((group) => group?.line_item_ids || List<number>())
        .flatten()
        .toArray() as number[],
    );
    return filteredEstimateLineItems(state, { ...props, estimateLineItemIds });
  },
);

/**
 * Sum of the cost of every line item (and its options) in the estimate — the `cost` variable the
 * formula / margin-with-overhead commission views feed into their calculations. Per-quantity options
 * scale by the line item's quantity; all other options use their own quantity only (rsf-app parity).
 *
 * @param {RootState} state The RootState
 * @param {number} estimateId The estimate id
 * @returns {number} the total cost (0 when the estimate is absent or has no line items)
 */
export const calculateEstimateCost = createSelector(
  [
    (state: RootState, estimateId: number) => estimate(state, { estimateId }),
    (state: RootState, estimateId: number) => lineItemsEstimate(state, { estimateId }),
    (state: RootState): Map<number, EstimateLineItemOptionRecord> => estimateLineItemOptionsById(state),
  ],
  (estimateRecord, lineItems, optionsById): number => {
    if (!estimateRecord) return 0;
    if (lineItems.isEmpty()) return 0;

    let sum = 0;
    lineItems.forEach((lineItem) => {
      sum += lineItem.get("cost") * lineItem.get("quantity");
      lineItem.get("option_ids").forEach((optionId: number) => {
        const option = optionsById?.get(optionId);
        // Skip unresolved option ids (no record for the id) AND soft-deleted (`_destroy`) options —
        // neither contributes to the estimate's cost.
        if (!option || option.get("_destroy")) return;
        // Byte-faithful port of rsf-app's calculateEstimateCost: per_quantity options scale by the
        // line item's quantity; every other option uses only its own quantity. This intentionally
        // does NOT route through the shared calculateQuantity (which additionally scales per_ui /
        // per_sqft) — commission-cost parity with rsf-app depends on this simpler rule, so don't
        // "fix" it into calculateQuantity.
        if (option.get("charge_type") === "per_quantity") {
          sum += option.get("quantity") * lineItem.get("quantity") * option.get("cost");
        } else {
          sum += option.get("quantity") * option.get("cost");
        }
      });
    });

    return sum;
  },
);

/**
 * Gets the current unsaved ID for creating new estimate line items
 *
 * @param {RootState} state The RootState
 * @returns {number} The current unsaved ID (negative number)
 */
export const unsavedId = (state: RootState): number => state.getIn([rootKey, "unsavedId"]) as number;

/**
 * Line item's images excluding `_destroy: true` and missing records.
 * See `specs/line-item-editor.md` → Selectors.
 *
 * @param {RootState} state The RootState
 * @param {{ estimateLineItemId: number }} props The estimate line item id
 * @returns {List<ImageRecord>} Active images attached to the line item
 */
export const activeLineItemImages = createSelector([estimateLineItem, imagesById], (lineItem, imagesMap) => {
  const ids = lineItem?.image_ids ?? List<number>();
  return ids
    .map((id: number) => imagesMap?.get(id) as ImageRecord | undefined)
    .filter((img: ImageRecord | undefined): img is ImageRecord => Boolean(img) && img!._destroy !== true)
    .toList();
});

/**
 * Line item's documents excluding `_destroy: true` and missing records.
 * Mirror of `activeLineItemImages`.
 *
 * @param {RootState} state The RootState
 * @param {{ estimateLineItemId: number }} props The estimate line item id
 * @returns {List<DocumentRecord>} Active documents attached to the line item
 */
export const activeLineItemDocuments = createSelector([estimateLineItem, documentsById], (lineItem, documentsMap) => {
  const ids = lineItem?.document_ids ?? List<number>();
  return ids
    .map((id: number) => documentsMap?.get(id) as DocumentRecord | undefined)
    .filter((doc: DocumentRecord | undefined): doc is DocumentRecord => Boolean(doc) && doc!._destroy !== true)
    .toList();
});

/**
 * First active image on a line item, or undefined. Used by the line-item card
 * so soft-deleted images don't render until save.
 *
 * @param {RootState} state The RootState
 * @param {{ estimateLineItemId: number }} props The estimate line item id
 * @returns {ImageRecord | undefined} The first active image, or undefined
 */
export const firstActiveLineItemImage = createSelector([activeLineItemImages], (images) => images.first<undefined>());

/**
 * Pure helper: per-line-item staging check. Shared between
 * `hasFileStagingForLineItem` and `hasFileStagingForEstimate` so neither has to
 * duplicate the doc/image scan logic.
 */
const lineItemHasFileStaging = (
  lineItem: Nullable<EstimateLineItemRecord>,
  docsMap: Map<number, DocumentRecord> | undefined,
  imagesMap: Map<number, ImageRecord> | undefined,
  lastSavedDocsMap: Map<number, DocumentRecord> | undefined,
  lastSavedImagesMap: Map<number, ImageRecord> | undefined,
): boolean => {
  if (!lineItem) return false;

  const docIds = lineItem.document_ids ?? List<number>();
  const imageIds = lineItem.image_ids ?? List<number>();

  const hasPendingDoc = docIds.some((id: number) => {
    if (id < 0) return true;
    const doc = docsMap?.get(id);
    if (doc?._destroy === true) return true;
    const lastSavedDoc = lastSavedDocsMap?.get(id);
    if (doc && lastSavedDoc && doc.display !== lastSavedDoc.display) return true;
    return false;
  });
  if (hasPendingDoc) return true;

  return imageIds.some((id: number) => {
    if (id < 0) return true;
    const img = imagesMap?.get(id);
    if (img?._destroy === true) return true;
    const lastSavedImg = lastSavedImagesMap?.get(id);
    if (img && lastSavedImg && img.display !== lastSavedImg.display) return true;
    return false;
  });
};

/**
 * True when the line item has pending file changes that need to flush on save:
 * a negative-ID record with `file_queue`, a positive-ID record marked
 * `_destroy: true`, or a display drift vs. `lastSavedById`. The display-drift
 * branch matters because dropdown writes target `byId` directly and would
 * otherwise be invisible to `isLineItemDirty` (which doesn't traverse links).
 * See `specs/line-item-editor.md` → Selectors.
 */
export const hasFileStagingForLineItem = createSelector(
  [
    estimateLineItem,
    documentsById,
    imagesById,
    (state: RootState) => state.getIn(["documents", "lastSavedById"]) as Map<number, DocumentRecord> | undefined,
    (state: RootState) => state.getIn(["images", "lastSavedById"]) as Map<number, ImageRecord> | undefined,
  ],
  (lineItem, docsMap, imagesMap, lastSavedDocsMap, lastSavedImagesMap) =>
    lineItemHasFileStaging(lineItem, docsMap, imagesMap, lastSavedDocsMap, lastSavedImagesMap),
);

/**
 * True when any line item in the estimate has pending file staging. Estimate-level
 * companion to `hasFileStagingForLineItem`. Plumbed into `useCurrentEstimateDirty`
 * so the header Save button enables on file-only changes (`isEstimateDirty` only
 * walks the estimate tree and is blind to doc/image mutations).
 *
 * Excludes line items currently in `editingSnapshotById` to honor the editing-session
 * exclusion invariant (DIRTY_TRACKING.md Level 1, ARCHITECT_BRIEF.md invariant #4):
 * an in-progress line item edit must not enable the header Save button until the
 * user commits the line item editor.
 *
 * @param {RootState} state The RootState
 * @param {{ estimateId: Nullable<number> }} props The estimate id
 * @returns {boolean}
 */
export const hasFileStagingForEstimate = createSelector(
  [
    (state: RootState, props: { estimateId: Nullable<number> }) =>
      props.estimateId ? estimate(state, { estimateId: props.estimateId }) : undefined,
    (state: RootState) => state.getIn(["estimateGroups", "byId"]) as Map<number, EstimateGroupRecord> | undefined,
    (state: RootState) => state.getIn(["estimateLineItems", "byId"]) as Map<number, EstimateLineItemRecord> | undefined,
    documentsById,
    imagesById,
    (state: RootState) => state.getIn(["documents", "lastSavedById"]) as Map<number, DocumentRecord> | undefined,
    (state: RootState) => state.getIn(["images", "lastSavedById"]) as Map<number, ImageRecord> | undefined,
    lineItemEditingSnapshots,
  ],
  (
    estimateRecord,
    groupsMap,
    lineItemsMap,
    docsMap,
    imagesMap,
    lastSavedDocsMap,
    lastSavedImagesMap,
    editingSnapshots,
  ): boolean => {
    if (!estimateRecord) return false;

    const editingIds: Set<number> =
      editingSnapshots && editingSnapshots.size > 0 ? Set(editingSnapshots.keySeq()) : Set();

    return (estimateRecord.group_ids ?? List<number>()).some((groupId: number) => {
      const group = groupsMap?.get(groupId);
      if (!group) return false;
      const lineItemIds = group.line_item_ids ?? List<number>();
      return lineItemIds.some((lineItemId: number) => {
        if (editingIds.has(lineItemId)) return false;
        const lineItem = lineItemsMap?.get(lineItemId);
        return lineItemHasFileStaging(lineItem, docsMap, imagesMap, lastSavedDocsMap, lastSavedImagesMap);
      });
    });
  },
);

/**
 * Returns all included, non-destroyed line items with validated === false for the given estimate.
 * Used by EstimateHeader to populate the ValidationErrorsModal and gate saving.
 *
 * @param {RootState} state The RootState
 * @param {{ estimateId: number }} props The estimate id
 * @returns {EstimateLineItemRecord[]} Array of failing line item records
 */
export const failingLineItemsForEstimate = createSelector(
  [
    (state: RootState, props: { estimateId: number }) => estimate(state, { estimateId: props.estimateId }),
    (state: RootState) => state.getIn(["estimateGroups", "byId"]) as Map<number, EstimateGroupRecord> | undefined,
    (state: RootState) => state.getIn(["estimateLineItems", "byId"]) as Map<number, EstimateLineItemRecord> | undefined,
  ],
  (estimateRecord, groupsMap, lineItemsMap): EstimateLineItemRecord[] => {
    if (!estimateRecord) return [];

    const failing: EstimateLineItemRecord[] = [];
    for (const groupId of estimateRecord.group_ids) {
      const group = groupsMap?.get(groupId);
      if (!group || !group.included || group._destroy) continue;
      for (const lineItemId of group.line_item_ids) {
        const lineItem = lineItemsMap?.get(lineItemId);
        if (lineItem && !lineItem._destroy && lineItem.validated === false) {
          failing.push(lineItem);
        }
      }
    }
    return failing;
  },
);
